import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { usePageTitle } from "@/components/layout/hooks/usePageTitle";
import { useCreateTeam } from "./hooks/useTeams";
import { UserSelectionList } from "./components/UserSelectionList";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useStaffSearch } from "./hooks/useStaff";

const teamSchema = z.object({
  name: z.string().min(1, "Team name is required"),
  userIds: z.array(z.string()).optional(),
  responsibleStaffId: z.string().optional(),
});

type TeamFormData = z.infer<typeof teamSchema>;

export const AddTeamPage: React.FC = () => {
  usePageTitle();
  const navigate = useNavigate();
  const createTeam = useCreateTeam();

  // Fetch all staff for the dropdown
  const { data: allStaff } = useStaffSearch({});

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<TeamFormData>({
    resolver: zodResolver(teamSchema),
    defaultValues: {
      name: "",
      userIds: [],
    },
  });

  const selectedUserIds = watch("userIds") || [];

    const allSelectedStaff = useMemo(
      () => allStaff?.filter((u) => selectedUserIds.includes(u.id)),
      [allStaff, selectedUserIds],
    );
  

  const onSubmit = async (data: TeamFormData) => {
    try {
      await createTeam.mutateAsync({
        name: data.name,
        userIds: data.userIds,
        responsibleStaffId: data.responsibleStaffId,
      });
      navigate("/staff/teams");
    } catch (error) {
      console.error("Failed to create team:", error);
    }
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-4xl mx-auto">
        <Button
          variant="ghost"
          className="gap-2 pl-0 hover:bg-transparent mb-6"
          onClick={() => navigate("/staff/teams")}
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </Button>

        <h1 className="text-2xl font-semibold text-center mb-8">Add team</h1>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
          <div className="space-y-2">
            <label
              htmlFor="name"
              className="text-sm font-medium uppercase text-gray-500"
            >
              Team Name
            </label>
            <Input
              id="name"
              {...register("name")}
              placeholder="Name"
              className="h-12 bg-white"
            />
            {errors.name && (
              <p className="text-sm text-red-500">{errors.name.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <label className="text-xl font-medium">Users</label>
            <UserSelectionList
              selectedUserIds={selectedUserIds}
              onSelectionChange={(ids) => setValue("userIds", ids)}
              className="h-[500px]"
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium uppercase text-gray-500">
              Responsible Staff
            </label>
            <Select
              value={watch("responsibleStaffId") || "none"}
              onValueChange={(value) =>
                setValue(
                  "responsibleStaffId",
                  value === "none" ? undefined : value,
                )
              }
            >
              <SelectTrigger className="h-12 bg-white justify-between w-full">
                <SelectValue placeholder="Select responsible staff" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                {allSelectedStaff?.map((user) => (
                  <SelectItem key={user.id} value={user.id}>
                    {user.firstName} {user.lastName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex justify-center pt-4">
            <Button
              type="submit"
              className="w-full max-w-xs bg-black hover:bg-gray-800 text-white h-12 text-base"
              disabled={isSubmitting || createTeam.isPending}
            >
              {isSubmitting || createTeam.isPending ? "Saving..." : "Save"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
